import type { ComponentType, ReactNode } from "react";
import {
  FileText,
  Plus,
  Receipt,
  Wrench,
  Calendar,
  CheckCircle2,
  MinusCircle,
} from "lucide-react";
import { cn, formatDate } from "@/lib/utils";
import type { LifecycleIconKey, LifecycleTint } from "@/lib/lifecycle-config";

// ADV-1347 (lifecycle epic): the generic, entity-agnostic lifecycle rail.
//
// Generalizes OpportunityLifecycleTimeline: the rail markup, hairline
// connector, marker, and title/time/detail row are identical and entity-
// neutral. The ONLY opportunity-coupling that lived in the old component — the
// hardcoded DISPATCH icon/tint map keyed by the opportunity event vocabulary
// — is removed. Callers resolve each event's `marker` (iconKey + tint) from
// LIFECYCLE_CONFIG *before* passing it here, so this component imports no
// config and renders ANY entity's lifecycle (job today, invoice/opportunity
// later). The empty-state copy is a prop so each entity teaches its own
// relationship (ADR-0019 §1.3 Type-2 empty state).

// iconKey → lucide icon. "faded, not flagged" (ADR-0019): color lives only on
// the marker; the chrome stays monochrome. The three tints below are the
// existing entity-agnostic semantic tokens.
const ICONS: Record<LifecycleIconKey, ComponentType<{ className?: string }>> = {
  created: Plus,
  scheduled: Calendar,
  started: Wrench,
  completed: CheckCircle2,
  signed_off: CheckCircle2,
  canceled: MinusCircle,
  proposal: FileText,
  invoice: Receipt,
  won: CheckCircle2,
  lost: MinusCircle,
};

const MARKER_TINT: Record<LifecycleTint, string> = {
  accent: "border-[color:var(--color-accent)] text-[color:var(--color-accent)]",
  success:
    "border-[color:var(--color-success)] text-[color:var(--color-success)]",
  muted: "border-[color:var(--color-border)] text-[color:var(--color-text-lo)]",
  warning:
    "border-[color:var(--color-warning)] text-[color:var(--color-warning)]",
  danger: "border-[color:var(--color-danger)] text-[color:var(--color-danger)]",
};

/**
 * A single rail item, already resolved to a marker. Callers map the uniform
 * server/derived `LifecycleEvent` shape into this via the config (see
 * `useEntityLifecycle` / `toTimelineItems`).
 */
export interface LifecycleTimelineItem {
  id: string;
  title: string;
  /** ISO timestamp; rendered absolute (§4.3). */
  time: string;
  /** Optional secondary line (job number, amount, status…). */
  detail?: string | null;
  marker: { iconKey: LifecycleIconKey; tint: LifecycleTint };
}

/**
 * The timestamped "moment" axis (decision T2), rendered oldest-first (earliest
 * → latest) so the rail reads as a chronological story top-to-bottom. A
 * vertical rail of icon markers connected by a hairline, each with a title, an
 * absolute date (§4.3), and an optional detail line. Purely props-driven over
 * already-resolved items. Renders a Type-2 teach line when there are no dated
 * milestones yet.
 */
export function LifecycleTimeline({
  items,
  emptyState,
}: {
  items: LifecycleTimelineItem[];
  /** Type-2 empty-state copy (string or node) teaching the relationship. */
  emptyState?: ReactNode;
}) {
  if (items.length === 0) {
    return (
      <p className="py-6 typo-label normal-case tracking-normal text-[color:var(--color-text-lo)]">
        {emptyState ??
          "Lifecycle milestones appear here as this record progresses — each step lands on the timeline as it happens."}
      </p>
    );
  }

  return (
    <ol className="relative">
      {items.map((item, i) => {
        const Icon = ICONS[item.marker.iconKey];
        const last = i === items.length - 1;
        return (
          <li key={item.id} className="relative flex gap-3 pb-5 last:pb-0">
            {/* Connecting hairline down to the next marker. */}
            {!last && (
              <span
                aria-hidden
                className="absolute left-3 top-6 h-full w-px -translate-x-1/2 bg-[color:var(--color-border)]"
              />
            )}
            <span
              className={cn(
                "z-10 flex size-6 shrink-0 items-center justify-center rounded-full border bg-[color:var(--color-surface)]",
                MARKER_TINT[item.marker.tint],
              )}
            >
              <Icon className="size-3.5" />
            </span>
            <div className="min-w-0 flex-1">
              <div className="flex items-baseline justify-between gap-2">
                <span className="typo-body text-[color:var(--color-text-hi)]">
                  {item.title}
                </span>
                <time className="shrink-0 typo-mono text-xs tabular-nums text-[color:var(--color-text-lo)]">
                  {formatDate(item.time)}
                </time>
              </div>
              {item.detail && (
                <span className="block truncate typo-label normal-case tracking-normal text-[color:var(--color-text-md)]">
                  {item.detail}
                </span>
              )}
            </div>
          </li>
        );
      })}
    </ol>
  );
}
